import CustomTabv2, { CustomTabItem } from 'components/custom/CustomTabv2';
import React, { MouseEvent, ReactNode } from 'react';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../helpers/StandardEditorContainerComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import ComponentName from '../../constant/ComponentName';
import { RootState } from '../../redux/store';
import { enterComponent, leaveComponent } from '../../redux/actions/ComponentActions';
import { clearPage } from '../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import CollectionNames from '../../constant/CollectionNames';
import RoleHelper from '../../RoleHelper';
import { changeAiModel, changeAiModelIsUpdate, clearAiModel } from '../../redux/actions/AiModelActions';
import ValueFactory from 'util/ValueFactory';
import { AiModelState } from '../../redux/reducers/AiModelReducer';
import { isStringValid } from '../../util/Generator';
import MessageUtil from '../../util/MessageUtil';
import LanguageUtil from '../../util/LanguageUtil';
import ComponentAuditLog from '../ComponentAuditLog';
import AiModelDatasourceFeaturesComponent from './detail/AiModelDatasourceFeaturesComponent';
import AiModelAccessComponent from './detail/AiModelAccessComponent';
import AiModelTrainingValidationComponent from './detail/AiModelTrainingValidationComponent';
import CustomLoading from '../custom/CustomLoading';
import Breadcrumb from '../Breadcrumb';
import CustomEditorItemComponent from '../custom/CustomEditorItemComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import AiModelService from '../../service/AiModelService';
import { ServiceNames } from '../../constant/ServiceNames';
import ServiceContainer from '../../config/ServiceContainer';
import { getComponentIcon } from '../../util/GetComponentIcon';
import { MLOpsModel, MLOpsDatasourceHistorical } from '../../domain/mlops/MLOpsDomains';
import AiModelFeaturesComponent from './detail/features/AiModelFeaturesComponent';
import MlOpsResultComponent from './detail/results/MLOpsResultComponent';
import { ConfirmPopup } from 'primereact/confirmpopup';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;

class AiModelEditorContainer extends StandardEditorContainerComponent<Props, State, MLOpsModel> {
  public state: State = new State();

  private aiModelSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private channelSubscription: subscribe.Subscription | undefined;
  private extraComputation: subscribe.Computation | undefined;

  private readonly aiModelService: AiModelService = ServiceContainer.getInstance().get(ServiceNames.AI_MODEL);
  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.AI_MODEL,
      icon: getComponentIcon(ComponentName.AI_MODEL),
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      aiModel: { aiModel },
      isUpdate,
    } = this.props;
    const { loading, extraLoading } = this.state;

    if (loading || extraLoading) {
      return <CustomLoading />;
    }

    return (
      aiModel != undefined && (
        <div className={'w-full h-full flex flex-col overflow-y-hidden '}>
          <Breadcrumb />
          <ConfirmPopup />
          <CustomEditorItemComponent
            name={aiModel.name}
            description={aiModel.description}
            status={aiModel.stage ? aiModel.stage : 'DRAFT'}
            isUpdate={isUpdate}
            component={ComponentName.AI_MODEL}
            icon={getComponentIcon(ComponentName.AI_MODEL)}
            onChangeName={this.onChangeName}
            onChangeDescription={this.onChangeDescription}
            onSave={this.saveItem}
            onDeploy={this.deployItem}
            onUndeploy={this.undeployItem}
            onRemove={this.removeItem}
            disableSave={!RoleHelper.canEdit(ComponentName.AI_MODEL)}
            disableRemove={!RoleHelper.canEdit(ComponentName.AI_MODEL)}
            disableNameChange={!RoleHelper.canEdit(ComponentName.AI_MODEL)}
            disableDescriptionChange={!RoleHelper.canEdit(ComponentName.AI_MODEL)}
            disableDeploy={!RoleHelper.canDeploy(ComponentName.AI_MODEL)}
            disableUndeploy={!RoleHelper.canDeploy(ComponentName.AI_MODEL)}
          />
          <div className={'flex flex-col overflow-y-scroll h-full w-full bg-white rounded'}>
            <CustomTabv2 tab={this.getTabs()} />
          </div>
        </div>
      )
    );
  }

  public async componentWillUnmount(): Promise<void> {
    const { clearAiModel } = this.props;
    clearAiModel();
    await super.componentWillUnmount();
  }

  protected _startTracker = (): void | Promise<void> => {
    const { changeAiModel, changeAiModelIsUpdate } = this.props;
    const variableId = this.getItemIdFromRoute();
    if (variableId == undefined) {
      changeAiModel(ValueFactory.initializeAiModel());
      changeAiModelIsUpdate(false);
    }
    const aiModelQuery = QueryBuilder.select().from(CollectionNames.AI_MODELS).where(eq('id', variableId)).build();
    const channelQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
    this.aiModelSubscription = subscribe.Formica.subscribe(CollectionNames.AI_MODELS, aiModelQuery);
    this.channelSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, channelQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.aiModelSubscription]);
    this.extraComputation = subscribe.Tracker.autorun(this.extraTracker, [this.channelSubscription]);
  };

  protected _stopTracker = async (): Promise<void> => {
    await this.aiModelSubscription?.stop();
    await this.channelSubscription?.stop();
    this.computation?.stop();
    this.extraComputation?.stop();
  };

  protected tracker = async (): Promise<void> => {
    const { changeAiModel, changeAiModelIsUpdate } = this.props;
    if (this.aiModelSubscription?.ready()) {
      const aiModelId = this.getItemIdFromRoute();
      if (aiModelId == undefined) {
        changeAiModelIsUpdate(false);
        RoleHelper.setCurrentAccess('Edit');
        this.setState({ loading: false });
        return;
      }
      const aiModelQuery = QueryBuilder.select().from(CollectionNames.AI_MODELS).where(eq('id', aiModelId)).build();
      const aiModel = await this.repository.find<MLOpsModel>(aiModelQuery);
      changeAiModel(aiModel || null);
      RoleHelper.setCurrentAccess(aiModel);
      this.setState({ loading: false });
    }
  };

  private extraTracker = () => {
    if (this.channelSubscription?.ready()) {
      this.setState({ extraLoading: false });
    }
  };

  protected getTabs = (): CustomTabItem[] => {
    const {
      aiModel: { aiModel, isUpdate },
    } = this.props;
    return [
      {
        key: 'Features',
        label: LanguageUtil.get({ id: 'common.tabs.features', defaultMessage: 'Features' }),
        content: <AiModelFeaturesComponent />,
      },
      {
        key: 'Datasource',
        label: LanguageUtil.get({ id: 'common.tabs.datasource', defaultMessage: 'Datasource' }),
        content: <AiModelDatasourceFeaturesComponent />,
        disable: !aiModel.channel,
      },
      {
        key: 'Training',
        label: LanguageUtil.get({ id: 'common.tabs.training', defaultMessage: 'Training' }),
        content: <AiModelTrainingValidationComponent />,
      },
      {
        key: 'Result',
        label: LanguageUtil.get({ id: 'common.tabs.result', defaultMessage: 'Result' }),
        content: <MlOpsResultComponent />,
        disable: !isUpdate,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <AiModelAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.AI_MODEL, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.AI_MODEL} documentName={this.props.aiModel.aiModel.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.AI_MODEL, true) && this.environmentUtil.isAuditLogVisible(),
      },
    ];
  };

  protected changeItem = (item: MLOpsModel): void => {
    const { changeAiModel } = this.props;
    changeAiModel(item);
  };

  private onChangeName = (name: string) => {
    const {
      changeAiModel,
      aiModel: { aiModel },
    } = this.props;
    if (isStringValid(name)) {
      aiModel.name = name.trim();
      changeAiModel(aiModel);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      changeAiModel,
      aiModel: { aiModel },
    } = this.props;
    aiModel.description = description;
    changeAiModel(aiModel);
  };

  protected getItemIdFromRoute = () => {
    const { changeAiModelIsUpdate } = this.props;
    const id = this.props.router.params.id;
    changeAiModelIsUpdate(id != undefined);
    return id;
  };

  private switchToUpdate = async () => {
    const {
      aiModel: { aiModel },
      router: { navigate, params },
      enterComponent,
    } = this.props;
    if (aiModel.name == '') return;
    enterComponent({ component: ComponentName.AI_MODEL, id: aiModel.name });
    navigate(`/ai-model${params.folderId ? `/folder/${params.folderId}` : ''}/edit/${aiModel.name}`, { replace: true });
    setTimeout(() => {
      this.computation?.onStop(() => this.startTracker());
      this.stopTracker();
    });
  };

  private saveItem = async () => {
    const {
      aiModel: { aiModel, trainingFile, validationFile },
      changeAiModelIsUpdate,
      isUpdate,
      lastCategory,
    } = this.props;
    this.clearHistoricalDatasourceQueries(aiModel);
    const result = await this.aiModelService.save(aiModel, isUpdate, lastCategory, trainingFile, validationFile);
    if (result) {
      changeAiModelIsUpdate(true);
      if (!isUpdate) await this.switchToUpdate();
    }
  };

  private clearHistoricalDatasourceQueries = (aiModel: MLOpsModel): void => {
    if (aiModel.trainingDatasource.type == 'HISTORICAL') {
      (aiModel.trainingDatasource as MLOpsDatasourceHistorical).query = ValueFactory.initializeFilter();
    }
    if (aiModel.validationDatasource.type == 'HISTORICAL') {
      (aiModel.validationDatasource as MLOpsDatasourceHistorical).query = ValueFactory.initializeFilter();
    }
  };

  private deployItem = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      aiModel: { aiModel },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => this.aiModelService.deploy(aiModel),
      message: LanguageUtil.get({ id: 'messages.confirm.deployAiModel' }),
    });
  };

  private undeployItem = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      aiModel: { aiModel },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => confirmUndeployWithImpact(ComponentName.AI_MODEL, [aiModel.name], () => this.aiModelService.undeploy(aiModel)),
      message: LanguageUtil.get({ id: 'messages.confirm.undeployAiModel' }),
    });
  };

  private removeItem = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      aiModel: { aiModel },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.aiModelService.remove(aiModel);
        if (success) {
          navigate('/ai-model');
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.removeAiModel' }),
    });
  };
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  loading: boolean = true;
  extraLoading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  aiModel: state.aiModel as AiModelState,
  isUpdate: state.aiModel.isUpdate,
  lastCategory: state.page.lastCategory,
});
const mapDispatchToProps = {
  changeAiModelIsUpdate,
  changeAiModel,
  clearAiModel,
  enterComponent,
  leaveComponent,
  clearPage,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(AiModelEditorContainer));
